/**
 * E013-T022: ZipAnalyzer
 *
 * Analyzes Notion ZIP exports to extract structure information.
 * Uses the Unzip class from upstream BlockSuite.
 * Handles nested ZIPs (Notion exports often have a wrapper ZIP).
 *
 * Single Responsibility: Only analyzes ZIP structure, does not extract content.
 */

import { Unzip } from '@blocksuite/affine/widgets/linked-doc';

import type {
  DatabaseInfo,
  IZipAnalyzer,
  NotionZipStructure,
  PageInfo,
  ZipFileEntry,
} from './types';

/**
 * Pattern to detect Notion database tables in HTML
 * Notion exports databases with this specific class
 */
const DATABASE_TABLE_PATTERN =
  /<table[^>]*class="[^"]*collection-content[^"]*"/i;

/**
 * Pattern to extract Notion UUID from filenames
 * Format: "Name UUID.html" where UUID is 32 hex chars
 */
const NOTION_UUID_PATTERN = /\s+([a-f0-9]{32})\.html$/i;

/**
 * Pattern to extract clean name from filename (without UUID and extension)
 */
const CLEAN_NAME_PATTERN = /^(.+?)\s+[a-f0-9]{32}\.html$/i;

export class ZipAnalyzer implements IZipAnalyzer {
  /**
   * Analyze a Notion ZIP export and extract its structure
   * Handles nested ZIPs automatically
   */
  async analyze(file: File): Promise<NotionZipStructure> {
    // First, try to extract and check for nested ZIP
    const fileEntries = await this.extractWithNestedSupport(file);

    const databases: DatabaseInfo[] = [];
    const pages: PageInfo[] = [];
    const folderPaths = new Set<string>();

    // E013-T028: Collect all files for attachment extraction
    const files: ZipFileEntry[] = fileEntries.map(entry => ({
      path: entry.path,
      name: entry.path.split('/').pop() || entry.path,
      blob: async () => entry.content,
    }));

    // First pass: collect folder paths
    for (const entry of fileEntries) {
      const lastSlash = entry.path.lastIndexOf('/');
      if (lastSlash > 0) {
        folderPaths.add(entry.path.substring(0, lastSlash));
      }
    }

    // Extract root name from first path
    const rootName = this.extractRootName(fileEntries);

    // Second pass: analyze each HTML file
    for (const entry of fileEntries) {
      if (!entry.path.toLowerCase().endsWith('.html')) {
        continue;
      }

      const htmlContent = await entry.content.text();
      const isDatabase = DATABASE_TABLE_PATTERN.test(htmlContent);

      const name = this.extractCleanName(entry.path);
      const notionId = this.extractNotionId(entry.path);

      if (isDatabase) {
        // Check if there's a matching folder for row pages
        const folderPath = entry.path.replace(/\.html$/i, '');
        const hasRowFolder = folderPaths.has(folderPath);

        // Count rows in the table
        const rowCount = this.countTableRows(htmlContent);

        databases.push({
          name,
          htmlPath: entry.path,
          htmlContent,
          rowPagesFolder: hasRowFolder ? folderPath : null,
          rowCount,
          notionId,
        });
      } else {
        // Check if this is a row page (inside a database folder)
        const isRowPage = this.isRowPage(entry.path, databases);

        if (!isRowPage) {
          pages.push({
            name,
            htmlPath: entry.path,
            notionId,
          });
        }
      }
    }

    console.log(
      `[ZipAnalyzer] Analyzed: ${databases.length} databases, ${pages.length} pages, ${files.length} files`
    );

    return {
      rootName,
      databases,
      pages,
      totalSize: file.size,
      files,
    };
  }

  /**
   * Extract ZIP contents, handling nested ZIPs
   * Notion exports sometimes wrap the actual export in another ZIP
   */
  private async extractWithNestedSupport(
    file: File
  ): Promise<Array<{ path: string; content: Blob }>> {
    const unzip = new Unzip();
    await unzip.load(file);

    const entries: Array<{ path: string; content: Blob }> = [];
    let nestedZip: Blob | null = null;

    // First pass: collect entries and look for nested ZIP
    for (const entry of unzip) {
      entries.push({
        path: entry.path,
        content: entry.content,
      });

      // Check if this is a nested ZIP (common in Notion exports)
      if (entry.path.toLowerCase().endsWith('.zip')) {
        console.log(`[ZipAnalyzer] Found nested ZIP: ${entry.path}`);
        nestedZip = entry.content;
      }
    }

    // If we only have a nested ZIP and no HTML files, extract the nested one
    const hasHtmlFiles = entries.some(e =>
      e.path.toLowerCase().endsWith('.html')
    );

    if (nestedZip && !hasHtmlFiles) {
      console.log('[ZipAnalyzer] Extracting nested ZIP...');
      const nestedUnzip = new Unzip();
      await nestedUnzip.load(nestedZip);

      const nestedEntries: Array<{ path: string; content: Blob }> = [];
      for (const entry of nestedUnzip) {
        nestedEntries.push({
          path: entry.path,
          content: entry.content,
        });
      }

      console.log(
        `[ZipAnalyzer] Nested ZIP contains ${nestedEntries.length} files`
      );
      return nestedEntries;
    }

    return entries;
  }

  /**
   * Extract root folder name from the first file path
   */
  private extractRootName(entries: Array<{ path: string }>): string {
    if (entries.length === 0) {
      return 'Notion Import';
    }

    const firstPath = entries[0].path;
    const parts = firstPath.split('/');

    if (parts.length > 0) {
      // Clean the root name (remove UUID if present)
      let rootName = parts[0];
      const uuidMatch = rootName.match(/^(.+?)\s+[a-f0-9]{32}$/i);
      if (uuidMatch) {
        rootName = uuidMatch[1];
      }
      return rootName || 'Notion Import';
    }

    return 'Notion Import';
  }

  /**
   * Extract clean name from file path (without UUID and extension)
   */
  private extractCleanName(path: string): string {
    // Get filename from path
    const lastSlash = path.lastIndexOf('/');
    const filename = lastSlash >= 0 ? path.substring(lastSlash + 1) : path;

    // Try to extract clean name (before UUID)
    const match = filename.match(CLEAN_NAME_PATTERN);
    if (match) {
      return match[1].trim();
    }

    // Fallback: just remove .html extension
    return filename.replace(/\.html$/i, '').trim();
  }

  /**
   * Extract Notion UUID from filename
   */
  private extractNotionId(path: string): string | null {
    const match = path.match(NOTION_UUID_PATTERN);
    return match ? match[1] : null;
  }

  /**
   * Count the number of rows in a database table
   */
  private countTableRows(html: string): number {
    // Count <tr> tags inside <tbody>
    const tbodyMatch = html.match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i);
    if (!tbodyMatch) {
      return 0;
    }

    const tbody = tbodyMatch[1];
    const trMatches = tbody.match(/<tr[^>]*>/gi);
    return trMatches ? trMatches.length : 0;
  }

  /**
   * Check if a page is a row page (inside a database's row folder)
   */
  private isRowPage(pagePath: string, databases: DatabaseInfo[]): boolean {
    for (const db of databases) {
      if (db.rowPagesFolder && pagePath.startsWith(db.rowPagesFolder + '/')) {
        return true;
      }
    }
    return false;
  }
}
